import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { startOfWeek, endOfWeek, subWeeks, format, eachDayOfInterval, parseISO } from 'date-fns';

interface WeeklyStats {
  weekStart: Date;
  totalTasks: number;
  completedTasks: number;
  mainTasksCompleted: number;
  focusMinutes: number;
  adminMinutes: number;
}

interface ProductivityStats {
  currentWeek: WeeklyStats;
  previousWeek: WeeklyStats;
  last4Weeks: WeeklyStats[];
  completionRate: number;
  completionTrend: 'up' | 'down' | 'stable';
  focusRatio: number;
  mainTaskCompletionRate: number;
  dailyCompletions: { date: string; completed: number; total: number }[];
}

export function useProductivityStats() {
  const [stats, setStats] = useState<ProductivityStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const timeout = setTimeout(() => setLoading(false), 15000);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setStats(null);
        clearTimeout(timeout);
        setLoading(false);
        return;
      }

      const now = new Date();
      const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
      const fourWeeksAgo = subWeeks(currentWeekStart, 4);

      // Fetch tasks and sessions for the last 4 weeks
      const [tasksResult, sessionsResult] = await Promise.all([
        supabase
          .from('tasks')
          .select('*')
          .eq('user_id', user.id)
          .gte('day', format(fourWeeksAgo, 'yyyy-MM-dd'))
          .lte('day', format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd')),
        supabase
          .from('focus_sessions')
          .select('*')
          .eq('user_id', user.id)
          .gte('started_at', fourWeeksAgo.toISOString())
          .lte('started_at', now.toISOString()),
      ]);

      const tasks = tasksResult.data || [];
      const rawSessions = sessionsResult.data || [];
      const sessions = rawSessions.filter((s) => s != null && s.started_at != null);

      // Calculate stats for each of the last 4 weeks
      const last4Weeks: WeeklyStats[] = [];
      for (let i = 0; i < 4; i++) {
        const weekStart = subWeeks(currentWeekStart, i);
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

        const weekTasks = tasks.filter(t => {
          if (!t.day) return false;
          const taskDate = parseISO(t.day);
          return taskDate >= weekStart && taskDate <= weekEnd;
        });

        const weekSessions = sessions.filter(s => {
          const sessionDate = new Date(s.started_at);
          return !Number.isNaN(sessionDate.getTime()) && sessionDate >= weekStart && sessionDate <= weekEnd;
        });

        const focusMins = (m: number) => (m || 0);
        last4Weeks.push({
          weekStart,
          totalTasks: weekTasks.length,
          completedTasks: weekTasks.filter(t => t.completed).length,
          mainTasksCompleted: weekTasks.filter(t => t.is_main_task && t.completed).length,
          focusMinutes: weekSessions.filter(s => s.work_type === 'focus').reduce((sum, s) => sum + focusMins(s.duration_minutes), 0),
          adminMinutes: weekSessions.filter(s => s.work_type === 'admin').reduce((sum, s) => sum + focusMins(s.duration_minutes), 0),
        });
      }

      const currentWeek = last4Weeks[0];
      const previousWeek = last4Weeks[1];

      // Calculate completion rate
      const completionRate = currentWeek.totalTasks > 0 
        ? (currentWeek.completedTasks / currentWeek.totalTasks) * 100 
        : 0;

      const prevCompletionRate = previousWeek.totalTasks > 0 
        ? (previousWeek.completedTasks / previousWeek.totalTasks) * 100 
        : 0;

      // Determine trend
      let completionTrend: 'up' | 'down' | 'stable' = 'stable';
      if (completionRate > prevCompletionRate + 5) completionTrend = 'up';
      else if (completionRate < prevCompletionRate - 5) completionTrend = 'down';

      // Focus ratio
      const totalMinutes = currentWeek.focusMinutes + currentWeek.adminMinutes;
      const focusRatio = totalMinutes > 0 ? (currentWeek.focusMinutes / totalMinutes) * 100 : 0;

      // Main task completion rate
      const allMainTasks = tasks.filter(t => t.is_main_task);
      const mainTaskCompletionRate = allMainTasks.length > 0
        ? (allMainTasks.filter(t => t.completed).length / allMainTasks.length) * 100
        : 0;

      // Daily completions for the current week
      const days = eachDayOfInterval({
        start: currentWeekStart,
        end: now,
      });

      const dailyCompletions = days.map(day => {
        const dayStr = format(day, 'yyyy-MM-dd');
        const dayTasks = tasks.filter(t => t.day === dayStr);
        return {
          date: dayStr,
          completed: dayTasks.filter(t => t.completed).length,
          total: dayTasks.length,
        };
      });

      setStats({
        currentWeek,
        previousWeek,
        last4Weeks,
        completionRate,
        completionTrend,
        focusRatio,
        mainTaskCompletionRate,
        dailyCompletions,
      });
    } catch (err) {
      console.error('Error fetching productivity stats:', err);
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, refetch: fetchStats };
}
